import type { Dispatch, SetStateAction } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import WorksheetButtons from 'modules/payroll_bundle/components/common/components/WorksheetButtons'
import type {
  ResultMatches,
  VisibleButtons,
  WorksheetData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'
import { Actions as ResultContextActions } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { Actions as BulkImportSupplementsContextActions } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { INITIAL_SELECTION_BUTTON_ID } from 'modules/payroll_bundle/lib/importConfig'
import { Actions as SepaContextActions } from 'modules/payroll_bundle/lib/sepaContext'

interface Props {
  worksheetData: WorksheetData
  worksheetMatches: ResultMatches
  visibleButtons: VisibleButtons[]
  setVisibleButtons: Dispatch<SetStateAction<VisibleButtons[]>>
  showDataTrimmingNotice?: boolean
  activeButton: string
  dispatch?:
    | React.Dispatch<BulkImportSupplementsContextActions>
    | React.Dispatch<ResultContextActions>
    | React.Dispatch<SepaContextActions>
  onClickButton?: (button: string) => void
  onWorksheetMatchesChange?: (newWorksheetMatches: ResultMatches) => void
}

const WorksheetHeader = ({
  worksheetData,
  worksheetMatches,
  setVisibleButtons,
  visibleButtons,
  showDataTrimmingNotice = false,
  activeButton,
  dispatch,
  onClickButton,
  onWorksheetMatchesChange,
}: Props) => {
  const setActiveButton = (value: string) => {
    if (dispatch) {
      dispatch({
        type: UPDATE_DATA,
        payload: {
          activeButton: value === activeButton ? '' : value,
        },
      })
    }
    onClickButton?.(value === activeButton ? '' : value)
  }

  const setWorksheetMatches = (newWorksheetMatches: ResultMatches) => {
    if (dispatch) {
      dispatch({
        type: UPDATE_DATA,
        payload: {
          worksheetMatches: newWorksheetMatches,
        },
      })
    }
    onWorksheetMatchesChange?.(newWorksheetMatches)
  }

  const resetAllButtons = () => {
    if (worksheetMatches && Object.keys(worksheetMatches).length > 0) {
      setActiveButton(INITIAL_SELECTION_BUTTON_ID)
    }
    setWorksheetMatches({})
  }

  const dataTrimmingMessage = () => {
    return (
      <Box
        background="tangerine200"
        height="s48"
        alignItems="center"
        flexDirection="row"
        paddingX="s24"
        gap="s8"
        borderRadius={{ all: 'abs016' }}
      >
        <Icon.Medium icon="alert-sign" color="tangerine1000" />
        <Text.Normal size="300" weight="normal">
          {i18n.t('payroll_bundle.payroll_results.stage_two.trimming_excel_warning_message')}
        </Text.Normal>
      </Box>
    )
  }

  return (
    <Box paddingBottom={'s4'}>
      <WorksheetButtons
        activeButton={activeButton}
        worksheetData={worksheetData}
        visibleButtons={visibleButtons}
        setActiveButton={setActiveButton}
        worksheetMatches={worksheetMatches}
        setVisibleButtons={setVisibleButtons}
        setWorksheetMatches={setWorksheetMatches}
        sequentialSelection
        onResetAllButtons={resetAllButtons}
      />
      {showDataTrimmingNotice && <Box paddingBottom="s16">{dataTrimmingMessage()}</Box>}
    </Box>
  )
}

export default WorksheetHeader
